import * as React from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { useIntl } from "react-intl";
import { toast } from "sonner";

import { ConfirmationModal } from "@/components/Modals/ConfirmationModal";
import { PageActionProps } from "@/components/PageTitle";
import { confirmChoice, confirmDialog } from "@/components/ui";
import { CoachSettings, SocialNetworksId, UploadProgressMap } from "@/interfaces";

import {
  URLS,
  editorQueueListUrl,
  capitalizeFormatter,
  formatHexColor,
  usePlayerSettings,
  useComplianceChecker,
  useSocialNetworks,
  useSocialCoachSession,
  getErrorMessage,
} from "@/utils";
import {
  ContentPageServices,
  EDITOR_VIDEO_PROJECT_EXTRA_FIELDS,
  Media,
  User,
  UsersServices,
  VideoProject,
  VideoProjectServices,
} from "@/services/bedrock";

import { EditorProjectFiles } from "./components/EditorProjectFiles";
import { EditorPostInfo } from "./components/EditorPostInfo";
import { CaptionCoachDialog } from "@/components/CaptionCoach/CaptionCoachDialog";
import { DuplicateVideoProjectModal } from "./components/DuplicateVideoProjectModal";
import { VideoProjectPageType, descriptors } from "./descriptors";
import { hasEditedVideo, prepareEditedSubtitle, toEditedMediaPayload } from "@/utils/editedDeliverables";
import { validateMediasRequiremenst } from "./validations";

import { VideoProjectBubbleDetails } from "./components/BubbleDetails";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { EnvContext } from "@/contexts/appContexts";
import { UploadProgressModal } from "@/components/UploadProgressModal";
import { buildPreferenceOptions, StylePreferencesOptions } from "@/utils/projectHelpers";
import { ClientNotesComponent } from "./components/ClientNotesComponent";
import { StylePreferencesComponent } from "./components/StylePreferencesComponent";
import { EditorPlayerInfo } from "./components/EditorPlayerInfo";
import { VideoProjectStatusColumnId } from "@/components/VideoProjectStatusPicker";
import { PageWrapper } from "@/components/PageWrapper";
import {
  assignedUserIdForRole,
  assigningRoleForEvent,
  confirmEventSamePersonAssignment,
  resolveStaffActorUserId,
} from "@/components/VideoProjectStaffPicker";
import { ProjectInfoCard } from "./components/ProjectInfoCard";
import { VideoProjectHistoryPanel, useInvalidateVideoProjectEventLogs } from "./components/VideoProjectHistoryPanel";
import { EditorFinalEdit } from "./components/EditorFinalEdit";
import { EditorProjectAlerts } from "./components/EditorProjectAlerts";
import { EditorSubmitEventDialog, EditorSubmittingDialog } from "./components/EditorSubmitDialogs";
import { VideoProjectQaStudio } from "./components/VideoProjectQaStudio";
import { isQaReviewStatus, isQaStudioReadOnly, shouldWarnSelfQa } from "./qaReview";
import { EditorVideoProjectField } from "./components/EditorCaptionSettings";
import { captureEditorDraft, EditorDraftSnapshot, hasEditorDraftChanges, restoreEditorDraft } from "./editorDraft";
import { applyEditorField, editorProjectUpdateFields } from "./editorFieldUpdates";
import { EditorPageActionSpec, getEditorPageActions } from "./pageActions";
import { resolveEditorStatusColumnAction } from "./statusColumnAction";
import { editorSubmitActionLabel, editorSubmitEvent, isEditorResubmit } from "./submitEvent";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export function EditorVideoProjectPage() {
  const history = useHistory();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const { formatMessage } = useIntl();
  const { userId, canManageVideoProjectQueue, session } = useSocialCoachSession();
  const envValues = React.useContext(EnvContext);
  const currentUserId = resolveStaffActorUserId(userId);
  const [openArchiveModal, setOpenArchiveModal] = React.useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = React.useState<boolean>(false);
  const [submitEventDialogOpen, setSubmitEventDialogOpen] = React.useState<boolean>(false);
  const [qaModalOpen, setQaModalOpen] = React.useState(false);
  const [startingQaReview, setStartingQaReview] = React.useState(false);
  const [reopeningProject, setReopeningProject] = React.useState(false);
  const [savingStatus, setSavingStatus] = React.useState(false);
  const queryClient = useQueryClient();
  const invalidateEventLogs = useInvalidateVideoProjectEventLogs();
  const autoOpenedQaRef = React.useRef(false);
  const { data: editorsData } = useQuery({
    queryKey: ["editors"],
    queryFn: () => UsersServices.getUsers({ userType: User.Type.EDITOR }),
    staleTime: 1000 * 60 * 30,
  });
  const staffUsers = editorsData?.content || [];
  const [loading, setLoading] = React.useState<boolean>(false);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [submittingProject, setSubmittingProject] = React.useState<boolean>(false);
  const [savingProject, setSavingProject] = React.useState<boolean>(false);
  const [savedDraft, setSavedDraft] = React.useState<EditorDraftSnapshot | null>(null);
  const lastSavedProjectRef = React.useRef<VideoProject | null>(null);
  const lastSavedHashtagsRef = React.useRef("");
  const [draftResetKey, setDraftResetKey] = React.useState(0);
  const [uploadProgressMap, setUploadProgressMap] = React.useState<UploadProgressMap>();
  const [formErrorMessages, setFormErrorMessages] = React.useState<string[] | undefined>(undefined);

  const [videoProject, setVideoProject] = React.useState<VideoProject | undefined>(undefined);
  const [videoProjectMessages, setVideoProjectMessages] = React.useState<VideoProject.Message[] | undefined>(undefined);
  const [needsAck, setNeedsAck] = React.useState<boolean>(false);
  const [playerNotes, setPlayerNotes] = React.useState<VideoProject.ClientNote | undefined>(undefined);
  const [coachNotes, setCoachNotes] = React.useState<VideoProject.ClientNote | undefined>(undefined);

  const [preferencesInfo, setPreferencesInfo] = React.useState<StylePreferencesOptions[]>([]);

  const isEditing = videoProject?.status === VideoProject.Status.EDITING;
  const isCompleted = VideoProject.isCompletedStatus(videoProject?.status);
  const editable = !!videoProject && !videoProject.archived && isEditing && !!videoProject.post;

  const [industryRole, setIndustryRole] = React.useState<string | null>(null);

  const [hashtags, setHashtags] = React.useState<string>("");
  const [initialHashtags, setInitialHashtags] = React.useState<string>("");
  const [showAIModal, setShowAIModal] = React.useState<boolean>(false);
  const [transcript, setTranscript] = React.useState<string | undefined>(undefined);
  const [blockedWordsInCaption, setBlockedWordsInCaption] = React.useState<string>();

  const { hasSetting } = usePlayerSettings(videoProject?.playerId);
  const showHashtagToggle = hasSetting(CoachSettings.PLAYER_HASHTAG, true);

  const { findBlockedWords, findFlaggedWords } = useComplianceChecker({
    playerId: videoProject?.playerId,
    coachId: videoProject?.coachId,
  });

  const { postableSocialNetworks } = useSocialNetworks(true, videoProject?.coachId);

  const validateMessage = (message?: string) => {
    const blockedWords = [...new Set([...findBlockedWords(message || ""), ...findFlaggedWords(message || "")])];
    if (blockedWords.length > 0) {
      setBlockedWordsInCaption(blockedWords.join(", "));
    } else {
      setBlockedWordsInCaption(undefined);
    }
  };

  React.useEffect(() => {
    const transcripts = videoProject?.post?.media
      ?.flatMap(m => m.transcripts?.map(t => t.content))
      ?.sort(t => t?.length || 0)
      ?.reverse();
    if (transcripts && transcripts.length > 0) {
      setTranscript(transcripts[0]);
    } else {
      setTranscript(undefined);
    }
  }, [videoProject?.post?.media]);

  const formDisabled = !editable;
  const hasUnsavedChanges = React.useMemo(() => {
    if (!videoProject) return false;
    return hasEditorDraftChanges(savedDraft, captureEditorDraft(videoProject, hashtags));
  }, [videoProject, hashtags, savedDraft]);

  const rememberSavedDraft = (project: VideoProject, tags: string) => {
    lastSavedProjectRef.current = project;
    lastSavedHashtagsRef.current = tags;
    setSavedDraft(captureEditorDraft(project, tags));
  };

  const cancelUnsavedChanges = () => {
    const savedProject = lastSavedProjectRef.current;
    if (!videoProject || !savedProject) return;

    setVideoProject(restoreEditorDraft(videoProject, savedProject));
    setHashtags(lastSavedHashtagsRef.current);
    setInitialHashtags(lastSavedHashtagsRef.current);
    setDraftResetKey(key => key + 1);
  };

  const requestCancelUnsavedChanges = async () => {
    if (!hasUnsavedChanges) return;

    const confirmed = await confirmDialog({
      title: "Discard unsaved changes?",
      description: "This will revert the caption, media, and posting settings to the last saved draft.",
      confirmLabel: "Discard changes",
      cancelLabel: "Keep editing",
      destructive: true,
    });
    if (!confirmed) return;

    cancelUnsavedChanges();
  };

  const applyLoadedProject = (nextProject: VideoProject) => {
    const tags = nextProject.post?.hashtags || "";
    setVideoProject(nextProject);
    setPlayerNotes(nextProject.clientNotes);
    setCoachNotes(nextProject.coachClientNotes);
    setVideoProjectMessages(nextProject.messages ?? []);
    setNeedsAck(!!nextProject.needsAck);
    setHashtags(tags);
    setInitialHashtags(tags);
    rememberSavedDraft(nextProject, tags);
  };

  const socialNetworksPosted = videoProject?.post?.postSocialNetworks || [];
  const [selectedSocialNetwork, setSelectedSocialNetwork] = React.useState<SocialNetworksId | undefined>(undefined);

  React.useEffect(() => {
    if (socialNetworksPosted.length > 0 && !selectedSocialNetwork) {
      setSelectedSocialNetwork(socialNetworksPosted[0]?.socialNetworkId);
    }
  }, [socialNetworksPosted]);

  const withCaption = React.useMemo(() => {
    return socialNetworksPosted.some(sn => sn.caption && sn.caption.length > 0);
  }, [socialNetworksPosted]);

  const finalMessage = React.useMemo(() => {
    if (withCaption) {
      const socialPost = socialNetworksPosted.find(sn => sn.socialNetworkId === selectedSocialNetwork);
      return socialPost?.caption || "";
    } else {
      return videoProject?.post?.message || "";
    }
  }, [selectedSocialNetwork, socialNetworksPosted, videoProject?.post?.message, withCaption]);

  React.useEffect(() => {
    validateMessage([videoProject?.post?.message, finalMessage, hashtags].filter(Boolean).join("\n"));
  }, [finalMessage, findBlockedWords, findFlaggedWords, hashtags, videoProject?.post?.message]);

  const hasCaptionCoach = React.useMemo(() => {
    return hasSetting(CoachSettings.CAPTION_COACH, true);
  }, [hasSetting]);

  const handleUpdateField = (field: EditorVideoProjectField, value: any) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return applyEditorField(prev, field, value);
    });
  };

  const handleMediaUpdated = (newMedia: Media[]) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        editedMedia: newMedia.map((item, sortOrder) => ({
          ...item,
          sortOrder,
        })),
      };
    });
  };

  React.useEffect(() => {
    const requestedId = params["id"];
    if (!requestedId) return;
    if (videoProject?.id === Number(requestedId)) return;

    setSelectedSocialNetwork(undefined);
    setLoading(true);
    VideoProjectServices.show(requestedId, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS)
      .then(nextProject => {
        applyLoadedProject(nextProject);
        setIndustryRole(nextProject.player?.industryRoles?.[0]?.name || null);
      })
      .catch(() => {
        alert("Error getting video project info");
        history.goBack();
      })
      .finally(() => {
        setLoading(false);
      });
    VideoProjectServices.acknowledge(Number(requestedId)).catch(() => undefined);
  }, [params["id"]]);

  const getErrors = (): string[] | undefined => {
    const errors: string[] = [];
    const validMedias = validateMediasRequiremenst(videoProject?.editedMedia);

    if (!validMedias) {
      errors.push(
        formatMessage({
          ...descriptors[VideoProjectPageType.wrongSpecificationsMessage],
        })
      );
    }

    if (videoProject?.editedMedia !== undefined && videoProject.editedMedia.length > 0) {
      videoProject.editedMedia.forEach(media => {
        if (!media.validAspectRatio && media.validAspectRatio !== undefined) {
          errors.push(
            formatMessage(
              {
                ...descriptors[VideoProjectPageType.wrongAspectRatioMessage],
              },
              { type: media.type.split("/")[0], typeAfterDot: capitalizeFormatter(media.type.split("/")[0]) }
            )
          );
        }

        if (media.errors?.wrongBitrate) {
          errors.push(
            formatMessage({
              ...descriptors[VideoProjectPageType.wrongBitrateMessage],
            })
          );
        }
        if (media.errors?.wrongLength) {
          errors.push(
            formatMessage({
              ...descriptors[VideoProjectPageType.wrongDurationoMessage],
            })
          );
        }
        if (media.errors?.wrongSize) {
          errors.push(
            formatMessage(
              {
                ...descriptors[VideoProjectPageType.wrongSizeMessage],
              },
              { type: media.type.split("/")[0], typeAfterDot: capitalizeFormatter(media.type.split("/")[0]) }
            )
          );
        }
      });
    }

    return errors.length > 0 ? errors : undefined;
  };

  React.useEffect(() => {
    setFormErrorMessages(() => getErrors());
  }, [videoProject]);

  const validateSubmit = (): boolean => {
    if (!videoProject) return false;

    const errors: string[] = [];
    if (!videoProject.post?.message || !videoProject.post.message.trim()) {
      errors.push("Caption is required");
    }

    if (!videoProject.editedMedia?.some(hasEditedVideo)) {
      errors.push("Final Edit is required");
    }

    if (errors.length > 0) {
      setFormErrorMessages(errors);
      return false;
    }

    setFormErrorMessages(undefined);
    return true;
  };

  const submitProject = async (
    event: VideoProject.Event.SUBMIT_FOR_QA_REVIEW | VideoProject.Event.COMPLETE,
    options?: { skipOptionalQaPrompt?: boolean }
  ): Promise<void> => {
    if (!videoProject || !validateSubmit()) return;

    if (event === VideoProject.Event.COMPLETE && !videoProject.requiresQa && !options?.skipOptionalQaPrompt) {
      setSubmitEventDialogOpen(true);
      return;
    }

    setSubmitEventDialogOpen(false);
    const submitEvent = event;

    const { editedMedia, playerId } = videoProject;
    const mediaToUpload = editedMedia?.filter(hasEditedVideo) ?? [];
    if (mediaToUpload.length === 0) return;

    const uploader = new ProjectFileUploader(envValues, session, "videoProject", playerId);
    const uploadMap: UploadProgressMap = {};
    mediaToUpload.forEach((mediaObj: Media) => {
      uploadMap[mediaObj.id] = { progress: 0, task: undefined };
    });
    setUploadProgressMap(uploadMap);
    setUploadingFiles(true);
    const preparedMedia = await uploader.prepareMedia(mediaToUpload, (fileId, progress) => {
      setUploadProgressMap(map => {
        return { ...map, [fileId]: { progress, task: undefined } };
      });
    });
    const editedSubtitle = await prepareEditedSubtitle(uploader, preparedMedia);
    setUploadingFiles(false);
    setSubmittingProject(true);

    const videoProjectId = videoProject.id;
    const contentPagesPromise = ContentPageServices.getContentPages({ videoProjectId, perPage: 100 });
    const submitViaEvents = async () => {
      const latest = await VideoProjectServices.show(videoProjectId);
      const isResubmit =
        latest.status === VideoProject.Status.READY_TO_POST ||
        latest.status === VideoProject.Status.SCHEDULED ||
        latest.status === VideoProject.Status.POST_FAILED ||
        latest.status === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW ||
        latest.status === VideoProject.Status.QA_REVIEW;

      if (latest.status === VideoProject.Status.SUBMITTED_FOR_EDITING) {
        if (!currentUserId) {
          throw new Error("Unable to start editing without a current user");
        }
        await VideoProjectServices.update(videoProjectId, {
          event: VideoProject.Event.START_EDITING,
          editorUserId: currentUserId,
        });
      }

      await VideoProjectServices.update(videoProjectId, {
        event: isResubmit ? VideoProject.Event.RESUBMIT : submitEvent,
        editedMedia: toEditedMediaPayload(preparedMedia),
        ...(editedSubtitle ? { editedSubtitle } : {}),
        ...editorProjectUpdateFields(videoProject, hashtags || undefined),
      });
    };

    Promise.all([contentPagesPromise, submitViaEvents()])
      .then(async ([contentPages]) => {
        if (contentPages.content.length > 0) {
          const originalMedia = preparedMedia.map(media => ({ ...media, id: -1 }));
          const updateContentPagePromises = contentPages.content.map(contentPage => {
            return ContentPageServices.updateContentPage(contentPage.id, { originalMedia });
          });
          await Promise.all(updateContentPagePromises);
        }
        toast.success("Project submitted successfully!");
        const nextProject = await VideoProjectServices.show(videoProjectId, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
        applyLoadedProject(nextProject);
        invalidateEventLogs(nextProject.id);
        queryClient.invalidateQueries({ queryKey: ["videoProjectAssignments", nextProject.id] });
      })
      .catch(e => {
        setFormErrorMessages([String(e).substring(0, 100)]);
      })
      .finally(() => {
        setSubmittingProject(false);
      });
  };

  const saveDraft = async (): Promise<void> => {
    if (!videoProject || !hasUnsavedChanges) return;

    const { editedMedia, playerId } = videoProject;
    const mediaToUpload = (editedMedia ?? []).filter(hasEditedVideo);

    setSavingProject(true);
    try {
      let preparedMedia = mediaToUpload;
      const uploader =
        mediaToUpload.length > 0 ? new ProjectFileUploader(envValues, session, "videoProject", playerId) : undefined;
      if (uploader) {
        const uploadMap: UploadProgressMap = {};
        mediaToUpload.forEach((mediaObj: Media) => {
          uploadMap[mediaObj.id] = { progress: 0, task: undefined };
        });
        setUploadProgressMap(uploadMap);
        setUploadingFiles(true);
        preparedMedia = await uploader.prepareMedia(mediaToUpload, (fileId, progress) => {
          setUploadProgressMap(map => {
            return { ...map, [fileId]: { progress, task: undefined } };
          });
        });
        setUploadingFiles(false);
      }

      const editedSubtitle = uploader ? await prepareEditedSubtitle(uploader, preparedMedia) : undefined;

      await VideoProjectServices.update(videoProject.id, {
        event: VideoProject.Event.SAVE_DRAFT,
        ...(preparedMedia.length > 0
          ? {
              editedMedia: toEditedMediaPayload(preparedMedia),
            }
          : {}),
        ...(editedSubtitle ? { editedSubtitle } : {}),
        ...editorProjectUpdateFields(videoProject, hashtags),
      });

      const nextProject = await VideoProjectServices.show(videoProject.id, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
      applyLoadedProject(nextProject);
      invalidateEventLogs(nextProject.id);
      queryClient.invalidateQueries({ queryKey: ["videoProjectAssignments", nextProject.id] });
      toast.success(formatMessage({ ...descriptors[VideoProjectPageType.savedMessage] }));
    } catch (error) {
      setFormErrorMessages([getErrorMessage(error) || String(error).substring(0, 100)]);
    } finally {
      setUploadingFiles(false);
      setSavingProject(false);
    }
  };

  const requestSaveDraft = async () => {
    if (!videoProject || !hasUnsavedChanges) return;

    const choice = await confirmChoice({
      title: "Save changes?",
      description: "Would you like to submit this project as well, or just save your draft?",
      confirmLabel: `Save and ${editorSubmitActionLabel(
        videoProject.requiresQa,
        isEditorResubmit({ qaStatus: videoProject.qaStatus, revisionCount: videoProject.revisionCount })
      )}`,
      cancelLabel: "Just save",
      size: "md",
    });
    if (choice === "dismiss") return;
    if (choice === "confirm") {
      await submitProject(editorSubmitEvent(videoProject.requiresQa));
      return;
    }

    await saveDraft();
  };

  const applyQueueUpdate = (updated: VideoProject) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: updated.status ?? prev.status,
        editorUserId: updated.editorUserId ?? prev.editorUserId,
        qaUserId: updated.qaUserId ?? prev.qaUserId,
      };
    });
    if (updated.id) {
      invalidateEventLogs(updated.id);
      queryClient.invalidateQueries({ queryKey: ["videoProjectAssignments", updated.id] });
    }
  };

  const reopenProject = async () => {
    if (!videoProject || !isCompleted || videoProject.archived) return;

    setReopeningProject(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event: VideoProject.Event.REOPEN_EDITING,
      });
      applyQueueUpdate(updated);
      toast.success("Project reopened for editing");
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to reopen project");
    } finally {
      setReopeningProject(false);
    }
  };

  const runStatusEvent = async (request: { event: VideoProject.Event; userId?: number }) => {
    if (!videoProject || videoProject.archived) return;

    const role = assigningRoleForEvent(request.event);
    const actorUserId = role
      ? assignedUserIdForRole({ editorUserId: videoProject.editorUserId, qaUserId: videoProject.qaUserId }, role) ??
        request.userId ??
        currentUserId
      : request.userId;
    if (role && actorUserId == null) {
      toast.error("Cannot update project status");
      return;
    }

    const confirmed = await confirmEventSamePersonAssignment(
      videoProject,
      request.event,
      actorUserId,
      staffUsers,
      currentUserId
    );
    if (!confirmed) return;

    if (request.event === VideoProject.Event.REJECT_QA || request.event === VideoProject.Event.APPROVE_QA) {
      void beginQaReview();
      return;
    }

    setSavingStatus(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event: request.event,
        ...(request.event === VideoProject.Event.START_EDITING && actorUserId != null
          ? { editorUserId: actorUserId }
          : {}),
      });
      applyQueueUpdate(updated);
      toast.success("Status updated successfully!");
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const beginQaReview = async () => {
    if (!videoProject || !canManageVideoProjectQueue || videoProject.archived) return;

    const isStartingQaReview = videoProject.status === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW;
    if (isStartingQaReview && shouldWarnSelfQa(currentUserId, videoProject.editorUserId)) {
      const confirmed = await confirmDialog({
        title: "Review your own edit?",
        description:
          "You are the assigned editor on this project. Reviewing your own cut can miss issues that another reviewer would catch.",
        confirmLabel: "Review anyway",
        cancelLabel: "Cancel",
      });
      if (!confirmed) return;
    }

    if (isStartingQaReview) {
      setStartingQaReview(true);
      try {
        const updated = await VideoProjectServices.update(videoProject.id, {
          event: VideoProject.Event.START_QA_REVIEW,
        });
        applyQueueUpdate(updated);
      } catch (error) {
        setFormErrorMessages([String(error).substring(0, 100)]);
        return;
      } finally {
        setStartingQaReview(false);
      }
    }

    setQaModalOpen(true);
  };

  const openQaStudio = () => setQaModalOpen(true);

  const handleStatusColumnSelect = (columnId: VideoProjectStatusColumnId) => {
    if (!videoProject) return;

    const action = resolveEditorStatusColumnAction(videoProject.status, columnId, {
      requiresQa: videoProject.requiresQa,
      currentUserId,
    });
    if (!action) return;

    if (action.type === "submit") {
      void submitProject(action.event);
      return;
    }

    if (action.type === "qa_review") {
      void beginQaReview();
      return;
    }

    void runStatusEvent(action);
  };

  React.useEffect(() => {
    const shouldOpenQa = new URLSearchParams(location.search).get("qa") === "1";
    if (!shouldOpenQa || autoOpenedQaRef.current || !videoProject || loading) return;
    autoOpenedQaRef.current = true;
    if (canManageVideoProjectQueue && isQaReviewStatus(videoProject.status) && !videoProject.archived) {
      void beginQaReview();
      return;
    }
    openQaStudio();
  }, [canManageVideoProjectQueue, loading, location.search, videoProject]);

  const isBusy =
    uploadingFiles || submittingProject || savingProject || startingQaReview || reopeningProject || savingStatus;

  const resolvePageAction = (spec: EditorPageActionSpec): PageActionProps => {
    const isLoading = loading || isBusy;
    const { intent } = spec;

    switch (intent.type) {
      case "qa_review":
        return { text: spec.text, onClick: () => void beginQaReview(), loading: startingQaReview };
      case "view_qa":
        return { text: spec.text, onClick: openQaStudio };
      case "reopen":
        return { text: spec.text, onClick: () => void reopenProject(), loading: reopeningProject };
      case "save":
        return {
          text: spec.text,
          onClick: () => void requestSaveDraft(),
          disabled: !hasUnsavedChanges || isLoading,
          loading: savingProject,
        };
      case "cancel":
        return {
          text: spec.text,
          onClick: () => void requestCancelUnsavedChanges(),
          disabled: !hasUnsavedChanges || isLoading,
        };
      case "submit":
        return { text: spec.text, onClick: () => void submitProject(intent.event), loading: isLoading };
      case "status_event":
        return {
          text: spec.text,
          onClick: () => void runStatusEvent({ event: intent.event, userId: intent.userId }),
          disabled: isLoading,
        };
      case "duplicate":
        return {
          text: spec.text,
          icon: spec.icon,
          onClick: () => setOpenDuplicateModal(true),
          disabled: isLoading,
        };
      case "archive":
        return {
          text: spec.text,
          icon: spec.icon,
          onClick: () => setOpenArchiveModal(true),
          disabled: isLoading,
        };
    }
  };

  const getPageActions = (): {
    primary?: PageActionProps;
    secondary?: PageActionProps;
    menu: PageActionProps[];
  } => {
    if (!videoProject) return { menu: [] };

    const mapping = getEditorPageActions({
      status: videoProject.status,
      archived: videoProject.archived,
      requiresQa: videoProject.requiresQa,
      qaStatus: videoProject.qaStatus,
      revisionCount: videoProject.revisionCount,
      canManageVideoProjectQueue,
      hasPost: !!videoProject.post,
      currentUserId,
      hasUnsavedChanges,
    });

    return {
      primary: mapping.primary ? resolvePageAction(mapping.primary) : undefined,
      secondary: mapping.secondary ? resolvePageAction(mapping.secondary) : undefined,
      menu: mapping.menu.map(resolvePageAction),
    };
  };

  const archiveModal = (): JSX.Element => {
    let title = formatMessage({ ...descriptors[VideoProjectPageType.archiveAlertTitle] });
    let okHandler = () => {
      VideoProjectServices.update(videoProject!.id, {
        event: VideoProject.Event.ARCHIVE,
      })
        .then(() => {
          history.push(editorQueueListUrl());
        })
        .catch((e: any) => {
          alert(e);
        });
    };

    return (
      <ConfirmationModal
        openConfirmationModal={openArchiveModal}
        title={title}
        onClose={() => setOpenArchiveModal(false)}
        rejectHandler={() => setOpenArchiveModal(false)}
        okHandler={okHandler}
      />
    );
  };

  const duplicateModal = (): JSX.Element | undefined => {
    if (!videoProject) return undefined;

    const onDuplicateHandler = (duplicatedProject: VideoProject) => {
      setOpenDuplicateModal(false);
      history.push(URLS.editor.videoProjects.detail.replace(":id", `${duplicatedProject.id}`));
    };

    return (
      <DuplicateVideoProjectModal
        open={openDuplicateModal}
        videoProjectId={videoProject.id}
        isVideoProjectArchived={!!videoProject.archived}
        onClose={() => setOpenDuplicateModal(false)}
        onDuplicate={onDuplicateHandler}
      />
    );
  };

  const refreshMessages = (): void => {
    VideoProjectServices.show(videoProject?.id!!, { messages: true }).then(r => {
      setVideoProjectMessages(r.messages ?? []);
    });
  };

  React.useEffect(() => {
    setPreferencesInfo(buildPreferenceOptions(videoProject?.preferences));
  }, [videoProject?.preferences]);

  const pageActions = getPageActions();

  return (
    <PageWrapper
      title={`Video Project #${videoProject?.id ?? ""}`}
      primaryAction={pageActions.primary ?? null}
      secondaryAction={pageActions.secondary}
      actions={pageActions.menu}
      loading={loading}
      breadcrumbs={[{ name: "Editing Queue", link: editorQueueListUrl() }]}
    >
      {archiveModal()}
      {duplicateModal()}

      <EditorSubmitEventDialog
        open={submitEventDialogOpen}
        onOpenChange={setSubmitEventDialogOpen}
        onSubmitForQa={() => void submitProject(VideoProject.Event.SUBMIT_FOR_QA_REVIEW)}
        onComplete={() => void submitProject(VideoProject.Event.COMPLETE, { skipOptionalQaPrompt: true })}
      />
      <EditorSubmittingDialog open={submittingProject} />

      {videoProject && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 flex flex-col gap-6">
              <EditorProjectAlerts
                videoProject={videoProject}
                isCompleted={isCompleted}
                formDisabled={formDisabled}
                formErrorMessages={formErrorMessages}
                reopeningProject={reopeningProject}
                onReopen={() => void reopenProject()}
                onReviewQa={openQaStudio}
              />
              <EditorPlayerInfo
                playerId={videoProject.playerId}
                industryRole={industryRole}
                videoProjectId={videoProject.id}
                coachId={videoProject.coachId}
              />

              <EditorProjectFiles videoProject={videoProject} />

              <EditorFinalEdit
                key={`final-edit-${draftResetKey}`}
                videoProject={videoProject}
                handleMediaUpdated={handleMediaUpdated}
                readOnly={formDisabled}
                onOpenQaStudio={openQaStudio}
              />

              <EditorPostInfo
                key={`post-info-${draftResetKey}`}
                videoProject={videoProject}
                disabled={formDisabled}
                hasCaptionCoach={!!hasCaptionCoach}
                showHashtagToggle={!!showHashtagToggle}
                postableSocialNetworks={postableSocialNetworks}
                finalMessage={finalMessage}
                hashtags={hashtags}
                initialHashtags={initialHashtags}
                setHashtags={setHashtags}
                onChangeField={handleUpdateField}
                setShowAIModal={setShowAIModal}
                blockedWordsInCaption={blockedWordsInCaption}
              />
            </div>
            <div className="lg:col-span-2 flex flex-col gap-6">
              <ProjectInfoCard
                videoProject={videoProject}
                setVideoProject={setVideoProject}
                statusDisabled={isBusy}
                onStatusColumnSelect={handleStatusColumnSelect}
                onQaFailedClick={openQaStudio}
              />
              <ClientNotesComponent
                playerId={videoProject?.playerId}
                coachId={videoProject?.coachId}
                playerNotes={playerNotes}
                coachNotes={coachNotes}
                onUpdate={(pNotes, cNotes) => {
                  setPlayerNotes(pNotes);
                  setCoachNotes(cNotes);
                }}
              />
              <StylePreferencesComponent preferencesInfo={preferencesInfo} formatHexColor={formatHexColor} />

              {videoProjectMessages && (
                <VideoProjectBubbleDetails
                  onSuccess={refreshMessages}
                  videoProjectId={videoProject.id}
                  submitBtnLabel={"Add Message"}
                  messages={videoProjectMessages}
                  needsAck={needsAck}
                />
              )}
              {canManageVideoProjectQueue && <VideoProjectHistoryPanel videoProjectId={videoProject.id} />}
            </div>
          </div>
          <VideoProjectQaStudio
            open={qaModalOpen}
            onOpenChange={setQaModalOpen}
            videoProject={videoProject}
            setVideoProject={setVideoProject}
            playerNotes={playerNotes}
            coachNotes={coachNotes}
            preferencesInfo={preferencesInfo}
            formatHexColor={formatHexColor}
            videoProjectMessages={videoProjectMessages}
            finalMessage={finalMessage}
            hashtags={hashtags}
            readOnly={isQaStudioReadOnly(videoProject.status, videoProject.archived)}
          />
        </>
      )}

      <UploadProgressModal open={uploadingFiles} progressMap={uploadProgressMap} />

      {showAIModal && (
        <CaptionCoachDialog
          open={showAIModal}
          onOpenChange={setShowAIModal}
          coachIdOverride={videoProject?.coachId}
          playerIdOverride={videoProject?.playerId}
          industryRoleOverride={industryRole || undefined}
          context={transcript}
          contextType="video transcript"
          handleAcceptedContent={({ caption: newCaption, hashtags: addedHashtags }) => {
            setShowAIModal(false);
            handleUpdateField("message", newCaption);

            if (addedHashtags && addedHashtags.length > 0) {
              const newTags: string[] = [];
              if (hashtags.length > 0) {
                hashtags.split(" ").forEach(tag => newTags.push(tag));
              }

              addedHashtags.forEach(tag => {
                if (newTags.indexOf(tag) === -1) {
                  newTags.push(tag);
                }
              });
              setHashtags(newTags.join(" "));
            }
          }}
        />
      )}
    </PageWrapper>
  );
}
